import {
  Box,
  Flex,
  FormControl,
  Link,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Stack,
} from '@chakra-ui/react';
import Stat from '@components/features/Stat';
import Carriers from '@components/features/landing-pages/BaseHero/Carriers';
import NumberInput from '@components/interactive/NumberInput';
import TextInput from '@components/interactive/TextInput';
import Accordion from '@components/layout/Accordion';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import Body1 from '@components/typography/Body1';
import Body2 from '@components/typography/Body2';
import Header from '@components/typography/Header';
import { antlerStyle } from '@constants/styles';
import useElementPosition from '@hooks/useElementPosition';
import { FsmColors } from 'chakra-ui/fsmTheme';
import { useRef, useState } from 'react';
import Confetti from 'react-confetti';
import CTAOutsidePeriod from './CTAOutsidePeriod';
import CTAWithinPeriod from './CTAWithinPeriod';
import calculate, { DEFAULT_WAGE_GROWTH_RATE } from './calculate';

const DOB_MIN = new Date('1900-01-01');
const ELIGIBILITY_AGE = 64.75;
const DOB_MAX = new Date();
const DEFAULT_RETIREMENT_AGE = 62;
const AGE_SLIDER_MIN = 620;
const AGE_SLIDER_MAX = 700;
const INPUT_FONT_SIZE = 20;

const faqs = [
  {
    header: 'How is this estimate calculated?',
    description: (
      <>
        {`This calculation can get a bit complicated, and so we made several assumptions. For one, we assumed that
      you worked continuously for the 35 years prior to retirement and your wages grew by
      ${DEFAULT_WAGE_GROWTH_RATE * 100}% each year throughout those years. For more information about the
      calculation, visit the`}{' '}
        <Link fontWeight="bold" color={FsmColors.navy} href="https://www.ssa.gov/OACT/quickcalc/index.html">
          Social Security Quick Calculator
        </Link>
      </>
    ),
  },
  {
    header: 'What is AIME?',
    description:
      "AIME stands for Average Indexed Monthly Earnings. It is your average earnings over your highest 35 years of work, adjusted for inflation using the Social Security Administration's wage-indexing system.",
  },
  {
    header: "Can I receive Social Security benefits based on my spouse's earnings?",
    description:
      "Yes, you may be eligible for spousal benefits based on your spouse's earnings record. The amount you receive will depend on your spouse's work history and the age at which you choose to start receiving benefits ranging from 32.5% to 50% of your spouse's primary insurance amount.",
  },
  {
    header: 'Can I work and still receive Social Security benefits?',
    description:
      'Yes, you can work and still receive Social Security benefits. However, if you start receiving benefits before your full retirement age and earn more than a certain amount, your benefits may be reduced.',
  },
  {
    header: 'What is the full retirement age for Social Security?',
    description:
      'The full retirement age (FRA) for Social Security benefits depends on your year of birth. For those born between 1943 and 1954, the FRA is 66. It gradually increases for those born after 1954, up to age 67 for those born in 1960 or later.',
  },
  {
    header: 'Do I need to sign up for Medicare before I retire?',
    description:
      "If you're enrolled in Social Security at least four months before your 65th birthday, you'll automatically be enrolled in Parts A and B. If you or your spouse has creditable coverage, then you can keep that as your primary paying insurance until you retire.",
  },
  {
    header: 'Can I delay my Social Security benefits to receive a higher monthly amount?',
    description:
      'Yes, you can delay your Social Security benefits up to age 70, and your monthly benefit amount will increase. However, you must weigh the benefits of receiving a higher monthly amount versus receiving benefits earlier and potentially receiving more total benefits over time.',
  },
  {
    header: 'What happens to my Social Security benefits if I pass away?',
    description:
      'Your Social Security benefits may be payable to your surviving spouse or children. The amount they receive will depend on your work history and their relationship to you.',
  },
  {
    header: 'How do you estimate my past earnings?',
    description: `Your historial earnings are calculated using the current salary value you entered and a ${
      DEFAULT_WAGE_GROWTH_RATE * 100
    }% wage increase percentage across the 35 years prior to retirement. This assumes you worked continuously for the 35 years prior to retirement. So, for example, if you took 5 years of (no income) during that period, this calculation would not be accurate. `,
  },
  {
    header: 'What earnings are covered under Social Security?',
    description:
      'Covered earnings refer to earnings that are subject to Social Security taxation, including most forms of wage income and self-employment income. While there are a few exceptions such as earnings from certain state and local governments and from railroads, today almost all types of wages are covered by Social Security.',
  },
];

// Format the salary for display
const formatCurrency = (value: number) => {
  return ` $${Math.trunc(value).toLocaleString('en-US')}`;
};

// Format date is yyyy-mm-dd
const formatDate = (date: Date) => {
  return date.toISOString().slice(0, 10);
};

const getAge = (dob: Date) => {
  if (!dob) return null;
  const diff = new Date().getTime() - dob.getTime();
  return diff / (1000 * 60 * 60 * 24 * 365);
};

// Returns the default date value for the DOB date input. 62 years before now.
const getDefaultDateValue = () => {
  const today = new Date();
  const sixtyTwoYearsAgo = new Date(today.getFullYear() - 62, today.getMonth(), today.getDate());
  const formattedDate = sixtyTwoYearsAgo.toISOString().slice(0, 10);
  return formattedDate;
};

const SocialSecurityCalculator = () => {
  const ref = useRef();
  const [dob, setDob] = useState<Date | null>();
  const [currentSalary, setCurrentSalary] = useState<number | null>(null);
  const [retirementAge, setRetirementAge] = useState<number | null>(DEFAULT_RETIREMENT_AGE);
  const [numberOfPieces, setNumberOfPieces] = useState(0);

  const thumbPosition = useElementPosition(ref, retirementAge);
  const age = getAge(dob);

  const results = calculate(currentSalary, dob, retirementAge);

  if (age && age < ELIGIBILITY_AGE) {
    return <CTAOutsidePeriod />;
  }

  return (
    <DefaultLayout>
      <Flex border="10px solid" borderColor={FsmColors.red} justifyContent="center">
        <Stack alignItems="center" px={antlerStyle.px} py={[12, 12, 20]} w="100%" maxW="1000px" spacing={6}>
          <Header as="h2" textAlign="center">
            When Should I Take Social Security?
          </Header>
          <Body1>
            If you're feeling lost and confused about when to claim your Social Security benefits, fear not! Enter your{' '}
            <b>Date of Birth</b> and <b>Current Salary</b>, and then use the slider to adjust your desired{' '}
            <b>Retirement Age.</b>
          </Body1>
          <Body1>
            Generally, the longer you wait to retire, the more social security benefits you will receive each month
            after retirement.
          </Body1>
          <Confetti
            numberOfPieces={numberOfPieces / 5}
            tweenDuration={500}
            colors={['#9DBB91']}
            initialVelocityY={10}
            drawShape={(ctx) => {
              ctx.fillRect(0, 0, 10, 20);
            }}
            confettiSource={{
              x: thumbPosition.x,
              y: thumbPosition.y,
              w: 10,
              h: 10,
            }}
            recycle
          />
          <Flex pb={10} maxW="600px" w="100%" pt={4}>
            <FormControl>
              <Stack w="100%" mx="auto" spacing={10}>
                <Stack>
                  <Body1 isBold>Date of Birth</Body1>
                  <TextInput
                    autoFocus
                    id="dob"
                    name="Date of Birth"
                    fontSize={INPUT_FONT_SIZE}
                    defaultValue={getDefaultDateValue()}
                    min="1910-01-01"
                    max={formatDate(new Date())}
                    onChange={(e: React.SyntheticEvent) => {
                      // Handle partial date inputs.
                      const target = e.target as HTMLInputElement;
                      try {
                        const newDate = new Date(target.value);
                        if (newDate >= DOB_MIN && newDate <= DOB_MAX) {
                          setDob(newDate);
                        } else {
                          setDob(null);
                        }
                      } catch (e) {
                        // ignore
                      }
                    }}
                    placeholder="Date of Birth"
                    type="date"
                    isRequired
                  />
                </Stack>
                <Stack>
                  <Body1 isBold>Current Salary</Body1>
                  <NumberInput
                    id="current-salary"
                    type="number"
                    fontSize={INPUT_FONT_SIZE}
                    min={10000}
                    format={(value: string) => formatCurrency(value ? parseInt(value) : 0)}
                    onChange={(value: string) => {
                      if (value) {
                        setCurrentSalary(parseInt(value));
                      }
                    }}
                    defaultValue={currentSalary}
                    step={1000}
                  />
                </Stack>
                <Stack spacing={4} pb={10}>
                  <Body1 isBold>Retirement Age</Body1>
                  <Box px={[5, 4, 0]}>
                    <Slider
                      zIndex={2}
                      size="lg"
                      min={AGE_SLIDER_MIN}
                      max={AGE_SLIDER_MAX}
                      defaultValue={retirementAge}
                      colorScheme={FsmColors.navy}
                      onChange={(value) => {
                        const retirementAge = value / 10;
                        setRetirementAge(retirementAge);
                        setNumberOfPieces(retirementAge);
                        setTimeout(() => {
                          setNumberOfPieces(0);
                        }, 1000);
                      }}
                      aria-label="slider-ex-1"
                    >
                      <SliderMark value={620} mt={10}>
                        <Body2 isBold>62</Body2>
                      </SliderMark>
                      <SliderMark value={660} mt={10}>
                        <Body2 isBold>66</Body2>
                      </SliderMark>
                      <SliderMark value={700} mt={10} ml="-20px">
                        <Body2 isBold>70</Body2>
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg={FsmColors.navy} />
                      </SliderTrack>
                      <SliderThumb ref={ref} bg={FsmColors.navy} boxSize={14}>
                        <Box fontWeight="bold" fontSize={30} color={FsmColors.white}>
                          {retirementAge && Math.trunc(retirementAge)}
                        </Box>
                      </SliderThumb>
                    </Slider>
                  </Box>
                </Stack>
                <Stack pb={10}>
                  <Body1 isBold>Your monthly social security check:</Body1>
                  <Stat>{formatCurrency(results || 0)}</Stat>
                </Stack>
                {age >= ELIGIBILITY_AGE ? <CTAWithinPeriod /> : <CTAOutsidePeriod />}
              </Stack>
            </FormControl>
          </Flex>
          <Stack pt={10} spacing={10}>
            <Header as="h3">Social Security Benefit FAQs</Header>
            <Accordion data={faqs} />
          </Stack>
        </Stack>
      </Flex>
      <Carriers />
    </DefaultLayout>
  );
};

export default SocialSecurityCalculator;
